import React, { useEffect } from 'react';
import {
  Box,
  Collapse,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Typography,
  SxProps,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { RightArrowIcon } from 'components/CommonIcons/Icons';
import { ReactComponent as RedirectIcon } from '../../assets/PepWise2.0/Redirect.svg';
import { ReactComponent as RedirectSelectedIcon } from '../../assets/PepWise2.0/RedirectSelected.svg';
import { useRecentVisited } from 'hooks/useRecentVisited';
import { useTheme } from "@mui/material/styles";

export type TileChild = {
  id: string;
  title: string;
  route: string;
  tagline?: string;
  icon?: React.ReactNode;
  iconKey?: string;
  hide?: boolean;
  children?: TileChild[];
};

export type DashboardTileProps = {
  id: string;
  title: string;
  route?: string;
  icon: React.ReactNode;
  iconKey?: string;
  childrenDashboards?: TileChild[];
  parentTagline?: string;
  onNavigate?: (route: string) => void;
  sx?: SxProps;
  open?: boolean;
  onToggle?: () => void;
};

const DashboardTileComponent: React.FC<DashboardTileProps> = ({
  id,
  title,
  route,
  icon,
  iconKey,
  childrenDashboards,
  parentTagline,
  onNavigate,
  sx,
  open = false,
  onToggle,
}) => {
  const theme = useTheme();
  const colors = theme.palette;
  const listRef = React.useRef<HTMLUListElement | null>(null);
  const containerRef = React.useRef<HTMLDivElement | null>(null);
  const rowRefs = React.useRef<Record<string, HTMLElement | null>>({});
  const [maxListHeight, setMaxListHeight] = React.useState(0);
  const isParent = (childrenDashboards?.length ?? 0) > 0;
  const HeaderIcon = icon;
  const { addVisited } = useRecentVisited();

  // Which second-level (nested) child row currently has its flyout open,
  const [openChildId, setOpenChildId] = React.useState<string | null>(null);
  const [subMenuTop, setSubMenuTop] = React.useState(0);

  const closeTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearCloseTimer = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  };

  const scheduleClose = () => {
    clearCloseTimer();
    closeTimeoutRef.current = setTimeout(() => {
      setOpenChildId(null);
    }, 150);
  };

  // Close the nested flyout whenever the parent tile collapses.
  useEffect(() => {
    if (!open) setOpenChildId(null);
  }, [open]);

  // Clear any pending close timer on unmount.
  useEffect(() => {
    return () => clearCloseTimer();
  }, []);

  const updateSubmenuPosition = (childId: string) => {
    const rowEl = rowRefs.current[childId];
    const containerEl = containerRef.current;
    if (rowEl && containerEl) {
      const rowRect = rowEl.getBoundingClientRect();
      const containerRect = containerEl.getBoundingClientRect();
      setSubMenuTop(rowRect.top - containerRect.top);
    }
  };

  const openSubmenuFor = (childId: string) => {
    clearCloseTimer();
    updateSubmenuPosition(childId);
    setOpenChildId(childId);
  };

  const goto = (
    event: React.MouseEvent,
    type: string,
    url: string,
    record?: {
      kind: 'parent' | 'child';
      title: string;
      tagline?: string;
      iconKey?: string;
      id: string;
    }
  ) => {
    event.preventDefault();
    if (!url) return;

    if (record) {
      addVisited({
        id: record.id,
        title: record.title,
        route: url,
        kind: record.kind,
        tagline: record.tagline,
        iconKey: record.iconKey
      });
    }

    if (type === 'blank') {
      window.open(url, '_blank');
    } else {
      if (onNavigate) onNavigate(url);
      else window.location.href = url;
    }
  };

  const handleHeaderClick = (e: React.MouseEvent) => {
    if (isParent) {
      if (onToggle) onToggle();
    } else if (route) {
      goto(e, 'no-blank', route, { kind: 'parent', id, title, iconKey });
    }
  };

  const handleHeaderContextMenu = (e: React.MouseEvent) => {
    if (!isParent && route) {
      e.preventDefault();
      goto(e, 'blank', route, { kind: 'parent', id, title, iconKey });
    }
  };

  useEffect(() => {
    if (!listRef.current) return;

    const calculateHeight = () => {
      const items = listRef.current?.querySelectorAll(".child-row-item") || [];
      let totalHeight = 0;

      for (let i = 0; i < Math.min(items.length, 3); i++) {
        totalHeight += items[i].getBoundingClientRect().height;
      }

      setMaxListHeight(totalHeight);
    };

    // Initial measurement
    calculateHeight();

    // Observe for dynamic size changes
    const observer = new ResizeObserver(calculateHeight);
    observer.observe(listRef.current);

    return () => observer.disconnect();
  }, [childrenDashboards, open]);

  return (
    <Box
      id="dashboard-tiles"
      ref={containerRef}
      sx={{
        width: '100%',
        overflow: 'visible',
        position: 'relative',
        minHeight: '62px',
        cursor: "pointer",
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        zIndex: open ? 1000 : 1,
        '&:hover': {
          transform: 'translateY(-5px)',
          zIndex: open ? 1000 : 20,
          '& > .tile-header': {
            boxShadow: '4px 8px 54px 0px #0000006B',
            height: '50px',
          }
        },
        ...sx
      }}
      onClick={handleHeaderClick}
      onContextMenu={handleHeaderContextMenu}
    >
      {/* Header */}
      <Box
        className="tile-header"
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 1.25,
          bgcolor: `${colors.landingPageTile.background} !important`,
          color: 'white',
          px: 2,
          py: 1.5,
          minHeight: '62px',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        }}
      >
        {/* icon before title */}
        <Box aria-hidden sx={{ display: 'grid', placeItems: 'center' }}>
          {HeaderIcon}
        </Box>

        {/* title + tagline stacked */}
        <Box sx={{ flex: 1 }}>
          <Typography
            variant="dashboardTileTitle"
            sx={{
              color: `${colors.landingPageTile.text} !important`
            }}
          >
            {title}
          </Typography>

          {parentTagline && (
            <Typography
              variant="dashboardTileCaption"
              sx={{
                color: `${colors.landingPageTile.parentTagline} !important`,
                mt: 0.5
              }}
            >
              {parentTagline}
            </Typography>
          )}
        </Box>

        {/* expand or open */}
        {isParent ? (
          <IconButton
            id="dashboard-plus-icon"
            size="small"
            aria-expanded={open}
            aria-controls={`${id}-children`}
            onClick={(e) => {
              e.stopPropagation();
              if (onToggle) onToggle();
            }}
            sx={{ color: `${colors.landingPageTile.ExpandIcon} !important` }}
          >
            {open ? (
              <RemoveIcon
                sx={{
                  color: `${colors.landingPageTile.ExpandIcon} !important`,
                  height: '24px',
                  width: '24px',
                }}
              />
            ) : (
              <AddIcon
                sx={{
                  color: `${colors.landingPageTile.ExpandIcon} !important`,
                  height: '24px',
                  width: '24px',
                }}
              />
            )}
          </IconButton>
        ) : (
          route && (
            <IconButton
              size="small"
              aria-label="Open"
              onClick={(e) =>
                goto(e, 'no-blank', route, {
                  kind: 'parent',
                  id,
                  title,
                  iconKey,
                })
              }
              onContextMenu={(e) => {
                e.preventDefault();
                goto(e, 'blank', route, {
                  kind: 'parent',
                  id,
                  title,
                  iconKey,
                });
              }}
            >
              <RedirectIcon height="24px" width="24px" />
            </IconButton>
          )
        )}
      </Box>

      {/* Children list */}
      {isParent && (
        <Collapse
          in={open}
          timeout="auto"
          unmountOnExit
          sx={{
            // MODIFICATION START: The following properties caused the clipping issue.
            position: 'absolute',
            zIndex: 99,
            boxShadow: 10,
            // MODIFICATION END
            width: '100%',
          }}
        >
          <List
            ref={listRef}
            id={`${id}-children`}
            dense
            disablePadding
            sx={{
              bgcolor: `${colors.landingPageTile.listBackground} !important`,
              py: 0.5,
              maxHeight: `${maxListHeight}px`,
              overflowY: 'auto',
              '&::-webkit-scrollbar': {
                width: '6px'
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: `${colors.scrollbar.thumb} !important`,
                borderRadius: '4px'
              },
              '&::-webkit-scrollbar-track': {
                backgroundColor: `${colors.scrollbar.background} !important`
              }
            }}
          >
            {childrenDashboards!.map((c) => {
              if (c.hide) return null;

              const hasSubmenu = (c.children?.length ?? 0) > 0;
              const isSubmenuOpen = hasSubmenu && openChildId === c.id;

              return (
                <ListItemButton
                  className="child-row-item"
                  key={c.id}
                  ref={(el) => {
                    rowRefs.current[c.id] = el;
                  }}
                  selected={isSubmenuOpen}
                  onMouseEnter={() => {
                    if (hasSubmenu) {
                      openSubmenuFor(c.id);
                    } else if (openChildId) {
                      clearCloseTimer();
                      setOpenChildId(null);
                    }
                  }}
                  onMouseLeave={() => {
                    if (hasSubmenu) {
                      scheduleClose();
                    }
                  }}
                  onClick={(e) =>
                    goto(e, 'no-blank', c.route, {
                      kind: 'child',
                      id: c.id,
                      title: c.title,
                      tagline: c.tagline,
                      iconKey: c.iconKey,
                    })
                  }
                  onContextMenu={(e) => {
                    e.preventDefault();
                    goto(e, 'blank', c.route, {
                      kind: 'child',
                      id: c.id,
                      title: c.title,
                      tagline: c.tagline,
                      iconKey: c.iconKey,
                    });
                  }}
                  sx={{
                    px: 2,
                    py: 1.25,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5
                  }}
                >
                  {/* left icon */}
                  <Box
                    aria-hidden
                    sx={{
                      display: 'grid',
                      placeItems: 'center',
                      mr: 1.5,
                      mt: 0.25,
                    }}
                  >
                    {c.icon}
                  </Box>

                  <ListItemText
                    primary={
                      <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
                        <Typography
                          variant='dashboardTileTitle'
                          sx={{
                            color: `${colors.landingPageTile.listitemColor} !important`
                          }}
                        >
                          {c.title}
                        </Typography>
                        {c.route === "/DAIXOps/summary/overview" && (
                          <sup style={{ color: "red", fontSize: "0.7em", marginLeft: "4px" }}>New</sup>
                        )}
                      </Box>
                    }
                    secondary={
                      c.tagline ? (
                        <Typography
                          variant="dashboardTileCaption"
                          sx={{
                            color: `${colors.landingPageTile.listitemColor} !important`,
                          }}
                        >
                          {c.tagline}
                        </Typography>
                      ) : null
                    }
                  />

                  <Box
                    sx={{
                      ml: 'auto',
                      flex: '0 0 auto',
                      display: 'grid',
                      placeItems: 'center'
                    }}
                  >
                    {hasSubmenu ? (
                      <RightArrowIcon color={colors.landingPageTile.listitemColor} />
                    ) : (
                      <RedirectSelectedIcon width="24px" height="24px" />
                    )}
                  </Box>
                </ListItemButton>
              );
            })}
          </List>
        </Collapse>
      )}

      {isParent &&
        open &&
        childrenDashboards!.map((c) => {
          if (!c.children?.length || openChildId !== c.id) return null;

          return (
            <Box
              key={`${c.id}-submenu`}
              id={`${c.id}-submenu`}
              onClick={(e) => e.stopPropagation()}
              onMouseEnter={clearCloseTimer}
              onMouseLeave={scheduleClose}
              sx={{
                position: 'absolute',
                top: `${subMenuTop}px`,
                left: 'calc(100% + 8px)',
                width: '100%',
                zIndex: 1001,
                boxShadow: '4px 8px 54px 0px #0000006B',
                overflow: 'hidden',
              }}
            >
              <List
                dense
                disablePadding
                sx={{
                  bgcolor: `${colors.landingPageTile.listBackground} !important`,
                  py: 0.5,
                }}
              >
                {c.children.map((nc) =>
                  !nc.hide ? (
                    <ListItemButton
                      key={nc.id}
                      onClick={(e) =>
                        goto(e, 'no-blank', nc.route, {
                          kind: 'child',
                          id: nc.id,
                          title: nc.title,
                          tagline: nc.tagline,
                          iconKey: nc.iconKey,
                        })
                      }
                      onContextMenu={(e) => {
                        e.preventDefault();
                        goto(e, 'blank', nc.route, {
                          kind: 'child',
                          id: nc.id,
                          title: nc.title,
                          tagline: nc.tagline,
                          iconKey: nc.iconKey,
                        });
                      }}
                      sx={{
                        px: 2,
                        py: 1.25,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                      }}
                    >
                      <Box
                        aria-hidden
                        sx={{
                          display: 'grid',
                          placeItems: 'center',
                          mr: 1.5,
                          mt: 0.25,
                        }}
                      >
                        {nc.icon}
                      </Box>

                      <ListItemText
                        primary={
                          <Typography
                            variant='dashboardTileTitle'
                            sx={{
                              color: `${colors.landingPageTile.listitemColor} !important`
                            }}
                          >
                            {nc.title}
                          </Typography>
                        }
                        secondary={
                          nc.tagline ? (
                            <Typography
                              variant="dashboardTileCaption"
                              sx={{
                                color: `${colors.landingPageTile.listitemColor} !important`,
                              }}
                            >
                              {nc.tagline}
                            </Typography>
                          ) : null
                        }
                      />

                      <Box
                        sx={{
                          ml: 'auto',
                          flex: '0 0 auto',
                          display: 'grid',
                          placeItems: 'center'
                        }}
                      >
                        <RedirectSelectedIcon width="24px" height="24px" />
                      </Box>
                    </ListItemButton>
                  ) : null
                )}
              </List>
            </Box>
          );
        })}
    </Box>
  );
};

export default DashboardTileComponent;
